import type { Book, PFOrder, Token } from "../api/types";
import { usePFBook, usePFCancel, usePFOrders, usePFStatus } from "../hooks/usePredict";
import { formatAge } from "../lib/format";
import { cmp, format, parse, sub } from "../lib/money";
import { useBooks } from "../store/useBooks";
import { Empty, Panel } from "./Panel";

/**
 * Котировки и ордера predict.fun рядом с окном Polymarket (ADR-0040).
 *
 * Слева — стаканы Up/Down пары и лучшие цены Polymarket того же исхода: ради
 * сравнения площадок проба и заведена. Справа — открытые ордера кошелька пробы
 * с кнопкой «снять». Пара ищется по slug окна, он у площадок общий.
 *
 * Окна близки, но не тождественны: predict.fun разрешает по Chainlink BTC/USDT,
 * Polymarket — по BTC/USD. Разница в цене исхода между площадками поэтому не
 * чистый арбитраж, о чём и говорит подзаголовок.
 */
export function PredictPanel({ slug, tokens }: { slug: string; tokens: Token[] }) {
  const book = usePFBook(slug);
  const status = usePFStatus();
  const orders = usePFOrders(Boolean(status.data?.address));
  const pf = book.data?.book ?? null;
  const age = pf ? Date.now() - Date.parse(pf.received_at) : null;

  const subtitle = pf
    ? `${pf.network} · ${pf.source === "ws" ? "лента" : "опрос"} · ${pf.trading_status}` +
      (age !== null ? ` · ${formatAge(age)} назад` : "") +
      " · разрешение по Chainlink USDT, не USD"
    : undefined;

  return (
    <Panel title="predict.fun" subtitle={subtitle} badge="проба" dense>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          {!pf ? (
            <Empty>
              {book.error ? `стакан не получен: ${book.error.message}` : (book.data?.reason ?? "загрузка…")}
            </Empty>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {(["Up", "Down"] as const).map((o, i) => (
                <OutcomeBook
                  key={o}
                  name={o}
                  tone={i === 0 ? "var(--color-buy)" : "var(--color-sell)"}
                  pf={o === "Up" ? pf.up : pf.down}
                  pmAsset={tokens[i]?.asset_id}
                />
              ))}
            </div>
          )}
        </div>
        <PFOrders
          orders={orders.data?.orders}
          error={
            orders.error?.message ?? (status.data && !status.data.address ? "ключ кошелька не задан" : null)
          }
          canCancel={Boolean(status.data?.trading)}
        />
      </div>
    </Panel>
  );
}

const DEPTH = 5;

function OutcomeBook({
  name,
  tone,
  pf,
  pmAsset,
}: {
  name: string;
  tone: string;
  pf: Book;
  pmAsset: string | undefined;
}) {
  // Строками, а не объектом: селектор по ссылке не перерисовывает на каждой
  // дельте чужого уровня.
  const pmBid = useBooks((s) => (pmAsset ? (s.books[pmAsset]?.bids[0]?.price ?? null) : null));
  const pmAsk = useBooks((s) => (pmAsset ? (s.books[pmAsset]?.asks[0]?.price ?? null) : null));
  // Пустая сторона стакана приходила null (Go nil-срез) и роняла страницу.
  const allAsks = pf.asks ?? [];
  const allBids = pf.bids ?? [];
  const asks = allAsks.slice(0, DEPTH).reverse();
  const bids = allBids.slice(0, DEPTH);
  const pfBid = allBids[0]?.price ?? null;
  const pfAsk = allAsks[0]?.price ?? null;

  return (
    <div className="min-w-0 text-xs">
      <div className="mb-1 font-medium" style={{ color: tone }}>
        {name.toUpperCase()}
      </div>
      <table className="num w-full">
        <tbody>
          {asks.map((l) => (
            <tr key={`a${l.price}`} className="text-[var(--color-sell)]">
              <td>{l.price}</td>
              <td className="text-right text-[var(--color-muted)]">{trimSize(l.size)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={2} className="border-t border-[var(--color-border)]" />
          </tr>
          {bids.map((l) => (
            <tr key={`b${l.price}`} className="text-[var(--color-buy)]">
              <td>{l.price}</td>
              <td className="text-right text-[var(--color-muted)]">{trimSize(l.size)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 text-[var(--color-muted)]">
        <span>predict</span>
        <span className="num text-right">
          {pfBid ?? "—"} / {pfAsk ?? "—"}
        </span>
        <span>polymarket</span>
        <span className="num text-right">
          {pmBid ?? "—"} / {pmAsk ?? "—"}
        </span>
        <span title="середина predict.fun минус середина Polymarket">Δ середин</span>
        <span className="num text-right">{midGap(pfBid, pfAsk, pmBid, pmAsk)}</span>
      </div>
    </div>
  );
}

function mid(bid: string | null, ask: string | null): number | null {
  const b = parse(bid);
  const a = parse(ask);
  if (b === null || a === null) return null;
  return Math.round((a + b) / 2);
}

function midGap(
  pfBid: string | null,
  pfAsk: string | null,
  pmBid: string | null,
  pmAsk: string | null,
): string {
  const a = mid(pfBid, pfAsk);
  const b = mid(pmBid, pmAsk);
  if (a === null || b === null) return "—";
  const d = sub(a, b);
  if (d === null) return "—";
  return `${cmp(d, 0) > 0 ? "+" : ""}${format(d)}`;
}

function trimSize(s: string): string {
  const v = parse(s);
  return v === null ? s : format(Math.trunc(v / 10_000) * 10_000);
}

function PFOrders({
  orders,
  error,
  canCancel,
}: {
  orders: PFOrder[] | undefined;
  error: string | null | undefined;
  canCancel: boolean;
}) {
  const cancel = usePFCancel();
  // Снятие, на которое площадка ответила «снят», а ордер успел исполниться,
  // — не успех: позиция появилась, и сказать об этом надо здесь же.
  const filledNote =
    cancel.data && cancel.data.filled.length > 0
      ? `исполнился до снятия: ${cancel.data.filled.join(", ")}`
      : null;
  return (
    <div className="min-w-0 text-xs">
      <div className="mb-1 font-medium text-[var(--color-muted)]">Ордера predict.fun</div>
      {error ? (
        <Empty>{error}</Empty>
      ) : !orders ? (
        <Empty>загрузка…</Empty>
      ) : orders.length === 0 ? (
        <Empty>открытых ордеров нет</Empty>
      ) : (
        <table className="num w-full">
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-[var(--color-border)] last:border-0">
                <td className="py-1" title={o.title ?? `рынок ${o.market_id}`}>
                  <span style={{ color: o.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)" }}>
                    {o.side}
                  </span>{" "}
                  {o.outcome.length > 8 ? `#${o.market_id}` : o.outcome.toUpperCase()}
                </td>
                <td>{o.price}</td>
                <td className="text-right">
                  {o.filled !== "0" ? `${o.filled}/` : ""}
                  {o.size}
                </td>
                <td className="text-right text-[var(--color-muted)]">
                  {o.slug ? o.slug.replace(/-updown-/, " ") : ""}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    disabled={!canCancel || cancel.isPending}
                    onClick={() => cancel.mutate(o.id)}
                    className="rounded border border-[var(--color-border)] px-1.5 text-[var(--color-muted)] hover:text-white disabled:opacity-40"
                    title={
                      canCancel
                        ? "снять со стакана площадки (не ончейн-отмена: подпись остаётся действительной до срока)"
                        : "торговля predict.fun выключена"
                    }
                  >
                    снять
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {cancel.error && <div className="mt-1 text-[var(--color-sell)]">{cancel.error.message}</div>}
      {filledNote && <div className="mt-1 text-amber-300">{filledNote}</div>}
    </div>
  );
}
